import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShoppingBag, Calendar, Ruler, Square, ArrowUpRight } from "lucide-react";
import { fmtNum, formatDate, STATUS_COLORS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <div className="bg-white border border-[#eae5dc] p-6">
      <div className="flex items-center justify-between">
        <span className="label-tag">{label}</span>
        <Icon size={18} className="text-[#c8a97e]" strokeWidth={1.5} />
      </div>
      <div className="font-serif text-4xl mt-4">{typeof value === "string" ? value : fmtNum(value)} <span className="text-base text-[#6b655c]">{suffix}</span></div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dealer/dashboard").then(({ data }) => setD(data));
  }, []);
  if (!d) return <div className="text-[#6b655c]">Yükleniyor…</div>;

  return (
    <div className="space-y-8" data-testid="dealer-dashboard">
      <div>
        <span className="label-tag">Hoşgeldiniz</span>
        <h1 className="font-serif text-4xl mt-2">{user?.company?.short_name || "Bayi Paneli"}</h1>
        <p className="text-[#6b655c] mt-1">Sipariş geçmişiniz ve istatistikleriniz</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={ShoppingBag} label="Toplam Sipariş" value={d.total_orders} />
        <Stat icon={Ruler} label="Toplam Uzunluk" value={(d.total_meters || 0) * 100} suffix="cm" />
        <Stat icon={Square} label="Toplam m²" value={d.total_sqm} suffix="m²" />
        <Stat icon={Calendar} label="Son Sipariş" value={d.last_order_date ? formatDate(d.last_order_date) : "—"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#eae5dc] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl">Son Siparişlerim</h3>
            <Link to="/bayi/siparislerim" className="text-xs uppercase tracking-[0.2em] text-[#c8a97e] hover:underline flex items-center gap-1">
              Tümü <ArrowUpRight size={14}/>
            </Link>
          </div>
          <div className="space-y-3">
            {d.recent_orders.length === 0 && <p className="text-[#6b655c] text-sm">Henüz sipariş yok</p>}
            {d.recent_orders.map((o) => (
              <Link key={o.id} to={`/bayi/siparislerim/${o.id}`} className="block border border-[#eae5dc] p-4 hover:border-[#c8a97e] transition-colors">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium">{o.order_no}</div>
                    <div className="text-xs text-[#6b655c]">{formatDate(o.created_at)} · {o.items?.length || 0} satır</div>
                  </div>
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-1 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                </div>
                <div className="mt-2 text-sm text-[#6b655c]">
                  {fmtNum((o.total_meters || 0) * 100)} cm · {fmtNum(o.total_sqm)} m²
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#eae5dc] p-6">
          <h3 className="font-serif text-xl mb-6">En Çok Sipariş Verdiğim</h3>
          <div className="space-y-4">
            {d.top_products.length === 0 && <p className="text-[#6b655c] text-sm">Veri yok</p>}
            {d.top_products.map((p, i) => (
              <div key={p.product_id} className="flex items-start justify-between gap-3 pb-4 border-b border-[#f0ebe2] last:border-0">
                <div className="flex gap-3">
                  <span className="text-[#c8a97e] font-serif text-xl w-6">{i + 1}</span>
                  <div>
                    <div className="font-medium text-sm">{p.name}</div>
                    <div className="text-xs text-[#6b655c]">{fmtNum(p.qty)} adet</div>
                  </div>
                </div>
                <div className="text-sm font-medium">{fmtNum(p.sqm)} m²</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
